import {
  Component,
  ViewChild,
  ElementRef,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnDestroy,
  ChangeDetectionStrategy
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { GlobalTemplateService } from '../../../../services/global-template.service';
import { MediaManagementService } from '../../../../services/media-management.service';
import { Subject, takeUntil } from 'rxjs';
import { TemporaryMedia } from '../../../../models/email-template/temporary-media.interface';
import { TypeEnum } from '../../../../models/template/type.enum';
import { TooltipModule } from '@openng/optimus-ui/tooltip';
import { META_CAROUSEL_MAX_IMAGE_KB, metaCarouselDimensionError } from '../../../../utils/meta-carousel-media-rules';

interface Dimension {
  width: number;
  height: number
}

const GEN_SINGLE_IMAGE_DIMENSIONS: Dimension[] = [
  { height: 628, width: 1200 },
  { height: 1200, width: 1200 },
  { height: 1200, width: 960 },
  { height: 600, width: 600 }
];

const PERFORMANCE_MAX_DIMENSIONS: Dimension[] = [
  { height: 628, width: 1200 },
  { height: 1200, width: 1200 },
  { height: 1200, width: 960 }
];

const META_SINGLE_IMAGE_DIMENSIONS: Dimension[] = [
  { height: 1080, width: 1080 },
  { height: 1920, width: 1080 }
];

@Component({
    selector: 'app-image-upload',
    imports: [CommonModule, TooltipModule],
    templateUrl: './image-upload.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: ['./image-upload.component.scss']
})
export class ImageUploadComponent implements OnInit, OnDestroy {
  constructor(
    private mediaManagementService: MediaManagementService,
    private globalTemplateService: GlobalTemplateService
  ) {
  }

  ngOnInit(): void {
    // Get template ID
    this.globalTemplateService.templateIdData$
      .pipe(takeUntil(this._destroy$))
      .subscribe((res: string) => {
        this.templateId = res;
      });

    // Get info if the template is new
    this.globalTemplateService.isNewTemplateData$
      .pipe(takeUntil(this._destroy$))
      .subscribe((res: boolean) => {
        this.isTemplateNew = res;
      });

    // Get template type
    this.globalTemplateService.templateTypeData$
      .pipe(takeUntil(this._destroy$))
      .subscribe((res: TypeEnum) => {
        this.templateType = res;
      });

    // Subscribe to temporary media array to see any new media has been uploaded
    this.updateImageSource();
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  @ViewChild('fileInput', { static: true })
  fileInput!: ElementRef<HTMLInputElement>;
  @Output() imageSrcEvent = new EventEmitter<string>();
  @Input() imageSrc: string | undefined;
  @Input() blockId: string | undefined;
  templateId: string | undefined;
  isTemplateNew: boolean = false;
  temporaryMedia: TemporaryMedia[] | undefined;
  private _destroy$ = new Subject<void>();
  templateType: TypeEnum | undefined;

  /** Shown inline next to the drop zone (AC6, RCA-397) - cleared on every new attempt. */
  protected uploadError: string | null = null;

  /**
   * Image compliance rules only apply to Google RSA Search templates, so the
   * requirements tooltip is shown for that type only.
   */
  get isGoogleRsaSearch(): boolean {
    return this.templateType === TypeEnum.GOOGLE_RSA_SEARCH;
  }

  /**
   * Subscribes to the `temporaryMediaData$` observable from `globalTemplateService`
   * and updates the `temporaryMedia` property with the latest temporary media.
   * If the component is handling a new template (`isTemplateNew === true`) and a `templateId` is present,
   * it checks if the current `imageSrc` matches any temporary media URI.
   * If a match is found and the temporary media item has a `newMediaURI`, it updates `imageSrc` accordingly.
   *
   * @private
   */
  private updateImageSource() {
    this.globalTemplateService.temporaryMediaData$
      .pipe(takeUntil(this._destroy$))
      .subscribe((res: TemporaryMedia[]) => {
        this.temporaryMedia = res;

        // Check if imageSrc has changed
        if (this.templateId && this.isTemplateNew === true) {
          const newImage = this.temporaryMedia.find(
            (tempMedia) => tempMedia.tempMediaURI === this.imageSrc
          );
          if (newImage && newImage.newMediaURI) {
            this.imageSrc = newImage.newMediaURI;
          }
        }
      });
  }

  /**
   * Emits the current image source (`imageSrc`) through the `imageSrcEvent` EventEmitter.
   * This method should be called whenever the image source changes and needs to be communicated
   * to parent components or other listeners.
   */
  emitImageSrc() {
    this.imageSrcEvent.emit(this.imageSrc);
  }

  /**
   * Handles the dragover event for the image upload component.
   *
   * @param event - The drag event triggered when an item is dragged over the drop zone.
   */
  onDragOver(event: DragEvent) {
    // Prevents the default browser behavior to allow custom drag-and-drop functionality.
    event.preventDefault();
  }

  /**
   * Handles the drop event for file uploads.
   * Prevents the default browser behavior and, if a file is present in the dropped data,
   * initiates loading of the first file using the `loadFile` method.
   *
   * @param event - The drag event containing the dropped files.
   */
  onDrop(event: DragEvent) {
    // Prevents the default browser behavior to allow custom drag-and-drop functionality.
    event.preventDefault();

    this.onDropForwardFile(event);
  }

  /**
   * Handles the image change event when a new image file is selected.
   * A compliant image is stored through MediaManagementService.storeImage - staged
   * until save for a new template, uploaded to Azure Storage right away otherwise.
   *
   * @param file - The selected image file to be processed.
   */
  async onImageChange(file: File) {
    this.uploadError = null;

    if (!(await this.isImageCompliant(file))) {
      return;
    }

    this.mediaManagementService
      .storeImage(file, this.blockId!, this.imageSrc)
      .pipe(takeUntil(this._destroy$))
      .subscribe({
        next: (imageSrc) => {
          this.imageSrc = imageSrc;
          this.emitImageSrc();
        },
        error: (err) => {
          console.error('Storing the image failed:', err);
        },
      });
  }

  private isAllowedFileSize(file: File, maxKb = 5120, maxLabel = `${maxKb}KB`): boolean {
    const fileSize = file.size / 1024;

    if (fileSize > maxKb) {
      this.uploadError = `File size is larger than ${maxLabel}!`;
      return false;
    }
    return true;
  }

  /**
   * Validates the pixel dimensions of an image file for Meta Carousel ads.
   *
   * @param file - The image file to validate.
   * @returns A promise resolving to `true` if the dimensions are allowed.
   */
  private async hasAllowedMetaCarouselDimensions(file: File): Promise<boolean> {
    let dimensions: { width: number; height: number };
    try {
      dimensions = await this.getImageDimensions(file);
    } catch {
      this.uploadError = "Could not read the image file!";
      return false;
    }

    const error = metaCarouselDimensionError('Image', dimensions.width, dimensions.height);
    if (error) {
      this.uploadError = error;
      return false;
    }
    return true;
  }

  /**
   * Validates the pixel dimensions of an image file: square 1:1 (min 300×300)
   * or landscape 1.91:1 (min 600×314). Reads the dimensions off the file and
   * shows an error toast if the file can't be read or the dimensions are not allowed.
   *
   * @param file - The image file to validate.
   * @returns A promise resolving to `true` if the dimensions are allowed.
   */
  private async hasAllowedRsaDimensions(file: File): Promise<boolean> {
    let dimensions: { width: number; height: number };
    try {
      dimensions = await this.getImageDimensions(file);
    } catch {
      this.uploadError = "Could not read the image file!";
      return false;
    }

    if (!this.isAllowedDimensions(dimensions.width, dimensions.height)) {
      this.uploadError = "Image must be square (min 300×300) or 1.91:1 (min 600×314)!";
      return false;
    }

    return true;
  }

  private async hasAllowedExactDimensions(file: File, allowedDimensions: Dimension[]): Promise<boolean> {
    let dimensions: { width: number; height: number };
    try {
      dimensions = await this.getImageDimensions(file);
    } catch {
      this.uploadError = "Could not read the image file!";
      return false;
    }

    if (!this.isAllowedExactDimensions(dimensions.width, dimensions.height, allowedDimensions)) {
      this.uploadError = "Image doesn't have the right dimensions!";
      return false;
    }
    return true;
  }

  private isCorrectExtension(file: File): boolean {
    const fileSplit = file.name.split('.');
    const fileSplitLength = fileSplit.length;
    const fileExtension = fileSplit[fileSplitLength - 1];

    if (fileExtension !== "png" && fileExtension !== "jpg") {
      this.uploadError = "File extension must be JPG or PNG!";
      return false;
    }

    return true;
  }

  private async isImageCompliant(file: File): Promise<boolean> {

    switch (this.templateType) {
      case TypeEnum.GOOGLE_RSA_SEARCH:
        // Accept only:
        // 1) JPG or PNG images
        // 2) Maximum file size 5120 KB
        // 3) Ratio has to be either 1x1 or 1.91x1 (within ~2% tolerance)
        // 4) Minimum pixels 300x300 (square) or 600x314 (landscape)
        if (!this.isCorrectExtension(file) || !this.isAllowedFileSize(file) || !(await this.hasAllowedRsaDimensions(file))) {
          return false;
        }
        break;
      case TypeEnum.GOOGLE_GEN_SINGLE_IMAGE:
        if (!(await this.hasAllowedExactDimensions(file, GEN_SINGLE_IMAGE_DIMENSIONS)) || !this.isAllowedFileSize(file)) {
          return false;
        }
        break;
      case TypeEnum.GOOGLE_PERFORMANCE_MAX:
        // JPG/PNG, max 5120 KB, exactly 1200x628, 1200x1200 or 960x1200
        if (!this.isCorrectExtension(file) || !this.isAllowedFileSize(file) || !(await this.hasAllowedExactDimensions(file, PERFORMANCE_MAX_DIMENSIONS))) {
          return false;
        }
        break;
      case TypeEnum.META_SINGLE_IMAGE:
        // JPG/PNG, max 5120 KB, exactly 1080x1080 or 1080x1920
        if (!this.isCorrectExtension(file) || !this.isAllowedFileSize(file) || !(await this.hasAllowedExactDimensions(file, META_SINGLE_IMAGE_DIMENSIONS))) {
          return false;
        }
        break;
      case TypeEnum.META_CAROUSEL:
        // JPG/PNG, max 30 MB, 1:1, 4:5 or 9:16 above the minimum size
        if (!this.isCorrectExtension(file) || !this.isAllowedFileSize(file, META_CAROUSEL_MAX_IMAGE_KB, '30 MB') || !(await this.hasAllowedMetaCarouselDimensions(file))) {
          return false;
        }
        break;
    }
    return true;
  }

  /**
   * Handles the drop event for forwarding a file.
   * Extracts the first file from the drag event and passes it to the image change handler.
   *
   * @param event - The drag event containing the dropped files.
   */
  private onDropForwardFile(event: DragEvent) {
    this.onImageChange(event.dataTransfer!.files[0]);
  }

  /**
   * Handles the click event for forwarding a file input.
   * Extracts the selected file from the event's target and passes it to the `onImageChange` handler.
   *
   * @param event - The click event triggered by the file input element.
   */
  onClickForwardFile(event: Event) {
    const input = event.target as HTMLInputElement;
    this.onImageChange(input.files![0]);

    // Reset the input so selecting the same file again still fires `change`.
    // Without this, re-picking an identical file is a no-op and validation
    // (size, extension, dimensions) never re-runs.
    input.value = '';
  }

  /**
   * Opens the native file selection dialog by programmatically triggering a click event
   * on the file input element referenced by `fileInput`.
   *
   * This method is typically used to allow users to select files for upload without
   * directly interacting with the file input element in the UI.
   */
  openFileDialog() {
    this.fileInput.nativeElement.click();
  }

  /**
   * Reads the pixel dimensions of an image file.
   *
   * Dimensions cannot be read off a raw `File` synchronously, so the file is
   * loaded into an `Image` and its natural size is read once it has loaded.
   *
   * @param file - The image file to measure.
   * @returns A promise resolving to the image's width and height in pixels.
   */
  private getImageDimensions(file: File): Promise<{ width: number; height: number }> {
    return new Promise((resolve, reject) => {
      const url = URL.createObjectURL(file);
      const img = new Image();
      img.onload = () => {
        URL.revokeObjectURL(url);
        resolve({ width: img.naturalWidth, height: img.naturalHeight });
      };
      img.onerror = () => {
        URL.revokeObjectURL(url);
        reject(new Error('Could not read image'));
      };
      img.src = url;
    });
  }

  /**
   * Validates that an image's dimensions match an allowed format:
   * square 1:1 (min 300×300) or landscape 1.91:1 (min 600×314).
   * The aspect ratio is matched within a ~2% tolerance.
   *
   * @param width - The image width in pixels.
   * @param height - The image height in pixels.
   * @returns `true` if the dimensions match an allowed format.
   */
  private isAllowedDimensions(width: number, height: number): boolean {
    const ratio = width / height;
    const tolerance = 0.02; // ~2%

    function matches(target: number): boolean {
      // 1) How far is the actual ratio from the target ratio?
      const difference = ratio - target;

      // 2) Ignore whether it's too wide or too tall — we only care about the size of the gap.
      const absoluteDifference = Math.abs(difference);

      // 3) How much gap are we willing to allow? 2% of the target.
      const allowedGap = target * tolerance;

      // 4) It "matches" if the gap is within the allowed amount.
      if (absoluteDifference <= allowedGap) {
        return true;
      } else {
        return false;
      }
    }

    const isSquare = matches(1) && width >= 300 && height >= 300;
    const isLandscape = matches(1.91) && width >= 600 && height >= 314;
    return isSquare || isLandscape;
  }

  private isAllowedExactDimensions(width: number, height: number, allowedDimensions: Dimension[]): boolean {
    let valid = false;
    allowedDimensions.forEach((dimension) => {
      if (dimension.width == width && dimension.height == height) {
        valid = true;
      }
    })

    if (!valid) {
      return false;
    }
    return true;
  }

}
